// src/pages/Billing.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Button,
} from '@mui/material';
import PlanCard from '../components/Billing/PlanCard';
import LoadingScreen from '../components/LoadingScreen';
import EmptyState from '../components/ui/EmptyState';
import {
  initiateBilling,
  fetchPlanStatus,
  fetchBillingHistory,
} from '../services/billingService';

const PLANS = [
  {
    id: 'basic',
    name: 'Básico',
    priceMonthly: 0,
    priceAnnual: 0,
    features: ['KPIs esenciales', 'Historial 3 meses', 'Soporte email'],
  },
  {
    id: 'pro',
    name: 'Profesional',
    priceMonthly: 79,
    priceAnnual: Math.round(79 * 12 * 0.8), // 20% off anual
    features: [
      'Hasta 5 proyectos',
      'Análisis avanzado',
      'Informes semanales',
      'Soporte prioritario',
      'Recomendaciones personalizadas',
    ],
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Empresarial',
    priceMonthly: 199,
    priceAnnual: Math.round(199 * 12 * 0.8),
    features: [
      'Proyectos ilimitados',
      'Análisis completo',
      'Informes diarios',
      'Soporte 24/7',
      'API acceso completo',
    ],
  },
];

function Billing() {
  const [period, setPeriod] = useState('monthly');
  const [planStatus, setPlanStatus] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar estado y facturación
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [statusRes, historyRes] = await Promise.all([
          fetchPlanStatus().catch(() => null),
          fetchBillingHistory().catch(() => []),
        ]);
        setPlanStatus(statusRes);
        setBillingHistory(historyRes);
      } catch (e) {
        setError('No se pudo cargar la información de facturación');
        console.error('[Billing] error:', e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleTogglePeriod = () => {
    setPeriod((prev) => (prev === 'monthly' ? 'annual' : 'monthly'));
  };

  const handleSelectPlan = async (planId) => {
    try {
      const { redirectUrl } = await initiateBilling(planId);
      window.location.href = redirectUrl;
    } catch (e) {
      alert('No se pudo iniciar el upgrade. Intenta más tarde.');
      console.error('[Billing] initiateBilling error:', e);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <Box p={2}>
      <Typography variant="h4" gutterBottom>
        Facturación y Planes
      </Typography>
      {error && <Typography color="error">{error}</Typography>}

      {/* Banner trial */}
      {planStatus?.plan === 'trial' && planStatus.trialDaysLeft > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Prueba gratuita</AlertTitle>
          Te quedan {planStatus.trialDaysLeft} día{planStatus.trialDaysLeft !== 1 && 's'} de prueba. Disfruta de todas las funciones premium.
          <Box mt={1} textAlign="right">
            <Button variant="contained" size="small" onClick={() => handleSelectPlan('pro')}>
              Upgrade ahora
            </Button>
          </Box>
        </Alert>
      )}

      {/* Toggle mensual/anual */}
      <Box textAlign="center" mb={2}>
        <FormControlLabel
          control={<Switch checked={period === 'annual'} onChange={handleTogglePeriod} />}
          label={period === 'annual' ? 'Anual (-20%)' : 'Mensual'}
        />
      </Box>

      {/* Planes */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={4}>
        {PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            id={plan.id}
            name={plan.name}
            priceMonthly={plan.priceMonthly}
            priceAnnual={plan.priceAnnual}
            period={period}
            features={plan.features}
            popular={plan.popular}
            onSelect={handleSelectPlan}
            selected={planStatus?.plan === plan.id || (plan.id==='basic' && planStatus?.plan==='trial')}
          />
        ))}
      </Stack>

      {/* Historial de facturación */}
      <Typography variant="h6" gutterBottom>
        Historial de Facturación
      </Typography>
      {billingHistory.length === 0 ? (
        <EmptyState message="Sin facturas aún" />
      ) : (
        <Card variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <CardContent sx={{ p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell>Concepto</TableCell>
                  <TableCell>Importe</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingHistory.map((item) => (
                  <TableRow key={item.number}>
                    <TableCell>{item.date}</TableCell>
                    <TableCell>{item.number}</TableCell>
                    <TableCell>{item.concept}</TableCell>
                    <TableCell>{`$${item.amount}`}</TableCell>
                    <TableCell>{item.status}</TableCell>
                    <TableCell align="right">
                      {item.invoiceUrl ? (
                        <a href={item.invoiceUrl} target="_blank" rel="noopener noreferrer">
                          Descargar
                        </a>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default Billing;
